/**
 * OSS前端直传工具
 * 使用STS临时凭证实现前端直传阿里云OSS
 */
import OSS from 'ali-oss'
import axios from 'axios'

let ossClient = null
let tokenExpiration = null

// 格式化时间为 yyyyMMddHHmmss
const formatDateTime = () => {
    const now = new Date()
    const pad = (n) => n.toString().padStart(2, '0')
    return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
}

/**
 * 获取或刷新OSS客户端
 */
export const getOssClient = async () => {
    // 检查token是否过期
    if (ossClient && tokenExpiration && new Date() < new Date(tokenExpiration)) {
        return ossClient
    }

    try {
        const res = await axios.get('/oss/sts-token')
        const response = res.data

        // 获取实际的 token 数据（可能在 data 字段中）
        const tokenData = response.data || response.results || response

        // 适配字段名：支持大驼峰和小驼峰两种格式
        const accessKeyId = tokenData.AccessKeyId || tokenData.accessKeyId
        const accessKeySecret = tokenData.AccessKeySecret || tokenData.accessKeySecret
        const stsToken = tokenData.SecurityToken || tokenData.securityToken
        const region = tokenData.Region || tokenData.region
        const bucket = tokenData.Bucket || tokenData.bucket
        const expiration = tokenData.Expiration || tokenData.expiration

        if (!accessKeyId || !accessKeySecret) {
            throw new Error('获取凭证失败：缺少必要的凭证信息')
        }

        ossClient = new OSS({
            region: region,
            accessKeyId: accessKeyId,
            accessKeySecret: accessKeySecret,
            stsToken: stsToken,
            bucket: bucket
        })

        tokenExpiration = expiration
        return ossClient
    } catch (e) {
        console.error('获取OSS凭证失败:', e)
        throw e
    }
}

// 清理文件名中的特殊字符（括号、空格等），避免 OSS CORS 问题
const sanitizeFileName = (name) => {
    return name.replace(/[()（）\[\]\s#%&{}|\\^~`@!$*+,;=]/g, '_')
}

/**
 * 普通上传（小文件）
 * @param {File} file - 文件对象
 * @param {string} path - 存储路径前缀
 * @returns {Promise<{url: string, name: string}>}
 */
export const uploadFile = async (file, path) => {
    const client = await getOssClient()
    const objectName = `${path}/${formatDateTime()}_${sanitizeFileName(file.name)}`
    const result = await client.put(objectName, file)

    // 确保 URL 是 https
    let url = result.url
    if (url.startsWith('http://')) {
        url = url.replace('http://', 'https://')
    }

    // 对 URL 进行签名（支持私有 Bucket）
    const signedUrl = await signFile(url)

    return {
        url: signedUrl,
        name: result.name,
        originalName: file.name
    }
}

// ali-oss multipartUpload 最小分片 100KB
const MIN_PART_SIZE = 100 * 1024

// 按文件大小自适应 partSize，保证既不雪崩也有足够进度粒度
const calcPartSize = (size) => {
    if (size < 5 * 1024 * 1024) return MIN_PART_SIZE
    if (size < 50 * 1024 * 1024) return 512 * 1024
    if (size < 200 * 1024 * 1024) return 1024 * 1024
    return 2 * 1024 * 1024
}

/**
 * 分片上传（带进度，基于 ali-oss multipartUpload 的真实分片完成回调）
 * @param {File} file - 文件对象
 * @param {string} path - 存储路径前缀
 * @param {Function} onProgress - 进度回调 (percent: number) => void
 * @returns {Promise<{url: string, name: string}>}
 */
export const uploadWithProgress = async (file, path, onProgress) => {
    const objectName = `${path}/${formatDateTime()}_${sanitizeFileName(file.name)}`
    const partSize = calcPartSize(file.size)

    console.log('[OSS Upload] Starting multipart upload:', objectName, 'size:', file.size, 'partSize:', partSize)

    // 判断是否为可重试的网络/超时错误
    const isRetryable = (e) => {
        if (!e) return false
        const name = e.name || ''
        const code = e.code || ''
        const msg = String(e.message || '')
        return (
            name === 'ConnectionTimeoutError' ||
            name === 'RequestTimeoutError' ||
            name === 'ResponseTimeoutError' ||
            code === 'ConnectionTimeoutError' ||
            code === 'RequestTimeoutError' ||
            code === 'ResponseTimeoutError' ||
            /timeout|network|socket|ECONN|ENETWORK/i.test(msg)
        )
    }

    // 带断点续传的重试：失败时用 e.checkpoint（或最近一次 progress 回传的 cpt）从断点继续
    const MAX_RETRY = 3
    let checkpoint = null
    let lastError = null
    let result = null
    for (let attempt = 0; attempt <= MAX_RETRY; attempt++) {
        // 每次重试前重新获取 client（getOssClient 内部会按过期自动刷新 STS token）
        const client = await getOssClient()
        try {
            result = await client.multipartUpload(objectName, file, {
                checkpoint,
                progress: async (p, cpt) => {
                    if (cpt) checkpoint = cpt
                    const percent = Math.round(p * 100)
                    console.log('[OSS Upload] Progress:', percent + '%')
                    onProgress && onProgress(percent)
                },
                partSize,
                parallel: 3,
                timeout: 120 * 1000
            })
            break
        } catch (e) {
            lastError = e
            // ali-oss 失败时通常会把 checkpoint 挂在 e 上，用它继续更可靠
            if (e && e.checkpoint) checkpoint = e.checkpoint
            if (!isRetryable(e) || attempt >= MAX_RETRY) {
                console.warn('[OSS Upload] Fatal error, giving up:', e && e.message)
                throw e
            }
            const backoff = 1000 * Math.pow(2, attempt)
            console.warn(`[OSS Upload] retrying (${attempt + 1}/${MAX_RETRY}) after ${backoff}ms:`, e && e.message)
            await new Promise(r => setTimeout(r, backoff))
        }
    }
    if (!result) throw lastError || new Error('上传失败')

    console.log('[OSS Upload] Complete')

    // multipartUpload 结果可能没有 res.requestUrls（小文件走简单 PUT 分支），兜底用 result.url
    let url = ''
    if (result.res && result.res.requestUrls && result.res.requestUrls.length) {
        url = result.res.requestUrls[0].split('?')[0]
    } else if (result.url) {
        url = result.url.split('?')[0]
    }
    if (url.startsWith('http://')) {
        url = url.replace('http://', 'https://')
    }

    // 对 URL 进行签名（支持私有 Bucket）
    const signedUrl = await signFile(url)

    return {
        url: signedUrl,
        name: result.name,
        originalName: file.name
    }
}

/**
 * 统一上传入口（真实进度）
 * 所有大小均走 multipartUpload：
 *   - <5MB 用 100KB 分片，保证 ~10 次进度回调
 *   - 大文件按 calcPartSize 自适应
 * @param {File} file - 文件对象
 * @param {string} path - 存储路径前缀
 * @param {Function} onProgress - 进度回调 (0~100)
 * @returns {Promise<{url: string, name: string}>}
 */
export const smartUpload = async (file, path, onProgress) => {
    return uploadWithProgress(file, path, onProgress)
}

/**
 * 删除 OSS 文件（通过后端 API）
 * @param {string} url - 文件 URL
 * @returns {Promise<boolean>}
 */
export const deleteFile = async (url) => {
    try {
        // 从 URL 中提取 object key
        // URL 格式: https://bucket.region.aliyuncs.com/path/to/file.jpg
        const urlObj = new URL(url)
        const objectKey = decodeURIComponent(urlObj.pathname.substring(1)) // 去掉开头的 /

        console.log('[OSS Delete] Deleting via backend API:', objectKey)

        const res = await axios.post('/pc/oss-delete', { objectKey })
        const data = res.data?.data || res.data

        if (data.success) {
            console.log('[OSS Delete] Success')
            return true
        } else {
            console.warn('[OSS Delete] Failed:', data.message)
            return false
        }
    } catch (e) {
        console.warn('[OSS Delete] Failed:', e.message)
        return false
    }
}

/**
 * 对 OSS URL 进行签名（通过后端 API）
 * @param {string} url - 原始 URL
 * @returns {Promise<string>} - 签名后的 URL
 */
export const signFile = async (url) => {
    try {
        if (!url) return url
        const res = await axios.post('/pc/oss-sign', { url })
        const data = res.data?.data || res.data
        if (data.success && data.signedUrl) {
            return data.signedUrl
        }
        return url
    } catch (e) {
        console.warn('[OSS Sign] Failed:', e)
        return url
    }
}

/**
 * 上传 Blob 数据（如录音）
 * @param {Blob} blob - Blob 数据
 * @param {string} path - 存储路径前缀
 * @param {string} filename - 文件名
 * @returns {Promise<{url: string, name: string}>}
 */
export const uploadBlob = async (blob, path, filename) => {
    const client = await getOssClient()
    const objectName = `${path}/${formatDateTime()}_${filename}`

    console.log('[OSS Upload] Uploading blob:', objectName, 'size:', blob.size)

    const result = await client.put(objectName, blob)

    // 确保 URL 是 https
    let url = result.url
    if (url.startsWith('http://')) {
        url = url.replace('http://', 'https://')
    }

    // 对 URL 进行签名（支持私有 Bucket）
    const signedUrl = await signFile(url)

    console.log('[OSS Upload] Blob upload complete:', signedUrl)

    return {
        url: signedUrl,
        name: result.name,
        originalName: filename
    }
}

/**
 * 初始化分片上传（获取uploadId）
 * @param {string} path - 存储路径前缀
 * @param {string} filename - 文件名
 * @returns {Promise<{uploadId: string, objectName: string}>}
 */
export const initMultipartUpload = async (path, filename) => {
    const client = await getOssClient()
    const objectName = `${path}/${formatDateTime()}_${filename}`

    console.log('[OSS Multipart] Initializing:', objectName)

    const result = await client.initMultipartUpload(objectName)

    console.log('[OSS Multipart] Initialized, uploadId:', result.uploadId)

    return {
        uploadId: result.uploadId,
        objectName: result.name
    }
}

/**
 * 上传单个分片
 * @param {string} objectName - 对象名称
 * @param {string} uploadId - 分片上传ID
 * @param {number} partNumber - 分片编号（从1开始）
 * @param {Blob} blob - 分片数据
 * @returns {Promise<{etag: string, partNumber: number}>}
 */
export const uploadPart = async (objectName, uploadId, partNumber, blob) => {
    const client = await getOssClient()

    console.log('[OSS Multipart] Uploading part:', partNumber, 'size:', blob.size)

    const result = await client.uploadPart(objectName, uploadId, partNumber, blob)

    console.log('[OSS Multipart] Part uploaded:', partNumber, 'etag:', result.etag)

    return {
        etag: result.etag,
        partNumber: partNumber
    }
}

/**
 * 完成分片上传（合并文件）
 * @param {string} objectName - 对象名称
 * @param {string} uploadId - 分片上传ID
 * @param {Array<{partNumber: number, etag: string}>} parts - 已上传分片列表
 * @returns {Promise<{url: string, name: string}>}
 */
export const completeMultipartUpload = async (objectName, uploadId, parts) => {
    const client = await getOssClient()

    console.log('[OSS Multipart] Completing upload:', objectName, 'parts:', parts.length, 'raw parts:', JSON.stringify(parts))

    // 验证并过滤有效的parts
    const validParts = parts.filter(p => {
        const isValid = p &&
            typeof p.partNumber === 'number' &&
            !isNaN(p.partNumber) &&
            p.partNumber > 0 &&
            typeof p.etag === 'string' &&
            p.etag.length > 0
        if (!isValid) {
            console.warn('[OSS Multipart] Invalid part:', p)
        }
        return isValid
    })

    if (validParts.length === 0) {
        throw new Error('没有有效的分片数据')
    }

    // 按partNumber去重（保留最后一个）并排序
    const partsMap = new Map()
    for (const part of validParts) {
        // ali-oss SDK 要求使用 number 属性（不是 partNumber）
        partsMap.set(part.partNumber, {
            number: part.partNumber,
            etag: part.etag.replace(/"/g, '') // 确保etag没有引号
        })
    }
    const sortedParts = Array.from(partsMap.values()).sort((a, b) => a.number - b.number)

    console.log('[OSS Multipart] Final sorted parts count:', sortedParts.length)
    console.log('[OSS Multipart] First 3 parts:', JSON.stringify(sortedParts.slice(0, 3)))
    console.log('[OSS Multipart] Last 3 parts:', JSON.stringify(sortedParts.slice(-3)))

    const result = await client.completeMultipartUpload(objectName, uploadId, sortedParts)

    // 确保 URL 是 https
    let url = result.res.requestUrls[0].split('?')[0]
    if (url.startsWith('http://')) {
        url = url.replace('http://', 'https://')
    }

    // 对 URL 进行签名
    const signedUrl = await signFile(url)

    console.log('[OSS Multipart] Complete:', signedUrl)

    return {
        url: signedUrl,
        name: result.name
    }
}

/**
 * 取消分片上传
 * @param {string} objectName - 对象名称
 * @param {string} uploadId - 分片上传ID
 */
export const abortMultipartUpload = async (objectName, uploadId) => {
    try {
        const client = await getOssClient()
        await client.abortMultipartUpload(objectName, uploadId)
        console.log('[OSS Multipart] Aborted:', objectName)
        return true
    } catch (e) {
        console.warn('[OSS Multipart] Abort failed:', e)
        return false
    }
}

/**
 * 列出已上传的分片
 * @param {string} objectName - 对象名称
 * @param {string} uploadId - 分片上传ID
 * @returns {Promise<Array<{partNumber: number, etag: string, size: number}>>}
 */
export const listParts = async (objectName, uploadId) => {
    try {
        const client = await getOssClient()
        const result = await client.listParts(objectName, uploadId)

        console.log('[OSS Multipart] Listed parts:', result.parts?.length || 0)

        return (result.parts || []).map(p => ({
            partNumber: p.partNumber,
            etag: p.etag,
            size: p.size
        }))
    } catch (e) {
        console.warn('[OSS Multipart] List parts failed:', e)
        return []
    }
}

/**
 * 计算文件指纹（用于断点续传识别）
 * 完整 SHA-256 在大文件下太慢，所以用：name + size + 首尾 256KB SHA-256
 * 同名同大小同首尾内容的文件，认为是同一份。
 */
export const computeFileFingerprint = async (file) => {
    const headSize = Math.min(256 * 1024, file.size)
    const tailSize = Math.min(256 * 1024, Math.max(0, file.size - headSize))
    const buffers = []
    if (headSize > 0) buffers.push(await file.slice(0, headSize).arrayBuffer())
    if (tailSize > 0) buffers.push(await file.slice(file.size - tailSize, file.size).arrayBuffer())

    const total = buffers.reduce((s, b) => s + b.byteLength, 0)
    const merged = new Uint8Array(total)
    let offset = 0
    for (const b of buffers) {
        merged.set(new Uint8Array(b), offset)
        offset += b.byteLength
    }

    const hashBuf = await crypto.subtle.digest('SHA-256', merged)
    const hex = Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, '0')).join('')
    const safeName = sanitizeFileName(file.name || 'file').slice(0, 32)
    return `${safeName}_${file.size}_${hex.slice(0, 24)}`
}

// ============ 后端会话（断点续传跨设备恢复）============

const sessionEndpoint = '/pc/oss-upload/session'

/**
 * 拿后端会话；不存在返回 null
 */
export const getUploadSession = async (businessKey) => {
    try {
        const res = await axios.get(sessionEndpoint, { params: { businessKey } })
        const data = res.data?.data || res.data
        if (data && data.success && data.exists) return data
        return null
    } catch (e) {
        console.warn('[OSS Resumable] getSession failed:', e?.message)
        return null
    }
}

/**
 * 初始化会话（后端会调 OSS InitiateMultipartUpload 拿 uploadId）
 */
export const initUploadSession = async (payload) => {
    const res = await axios.post(`${sessionEndpoint}/init`, payload)
    const data = res.data?.data || res.data
    if (!data || !data.success) throw new Error('初始化上传会话失败')
    return data
}

export const completeUploadSession = async (businessKey) => {
    try {
        await axios.post(`${sessionEndpoint}/complete`, { businessKey })
    } catch (e) {
        console.warn('[OSS Resumable] complete session failed:', e?.message)
    }
}

export const abortUploadSession = async (businessKey) => {
    try {
        await axios.post(`${sessionEndpoint}/abort`, { businessKey })
    } catch (e) {
        console.warn('[OSS Resumable] abort session failed:', e?.message)
    }
}

/**
 * 可断点续传上传（跨设备）。
 * - businessKey 为业务唯一键：建议 `${answerKey}_${itemKey}_${fileFingerprint}`
 * - 进度由 listParts 的已上传字节 + 当前轮次进度合算
 *
 * @param {File} file
 * @param {string} path
 * @param {string} businessKey
 * @param {Function} onProgress (percent: number) => void
 * @param {object} [opts] { sessionType=0, mimeType, extraJson, createdBy }
 * @returns {Promise<{url, name, originalName}>}
 */
export const uploadResumable = async (file, path, businessKey, onProgress, opts = {}) => {
    if (!businessKey) throw new Error('businessKey 不能为空')
    const sessionType = opts.sessionType ?? 0
    const partSize = calcPartSize(file.size)
    const fingerprint = await computeFileFingerprint(file)

    let session = await getUploadSession(businessKey)
    let uploadedParts = session ? (session.uploadedParts || []) : []
    let objectName = session?.objectName
    let uploadId = session?.uploadId

    const fingerprintMismatch = session && session.fileHash && session.fileHash !== fingerprint
    if (fingerprintMismatch) {
        console.warn('[OSS Resumable] fingerprint mismatch, abort old session')
        await abortUploadSession(businessKey)
        session = null
        uploadedParts = []
        objectName = null
        uploadId = null
    }

    if (!session) {
        objectName = `${path}/${formatDateTime()}_${sanitizeFileName(file.name)}`
        const initRes = await initUploadSession({
            BusinessKey: businessKey,
            SessionType: sessionType,
            ObjectName: objectName,
            FileSize: file.size,
            FileHash: fingerprint,
            MimeType: opts.mimeType || file.type || '',
            ExtraJson: opts.extraJson || null,
            CreatedBy: opts.createdBy || null
        })
        uploadId = initRes.uploadId
        objectName = initRes.objectName
    }

    const uploadedSet = new Map()
    for (const p of uploadedParts) uploadedSet.set(p.partNumber, p)

    const totalParts = Math.ceil(file.size / partSize) || 1
    const reportProgress = () => {
        if (!onProgress) return
        let uploaded = 0
        for (const p of uploadedSet.values()) uploaded += p.size || 0
        const percent = Math.min(99, Math.round((uploaded / Math.max(1, file.size)) * 100))
        onProgress(percent)
    }
    reportProgress()

    const client = await getOssClient()
    const MAX_PART_RETRY = 3

    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        if (uploadedSet.has(partNumber)) continue
        const start = (partNumber - 1) * partSize
        const end = Math.min(start + partSize, file.size)
        const blob = file.slice(start, end)

        let lastErr = null
        for (let attempt = 0; attempt <= MAX_PART_RETRY; attempt++) {
            try {
                const cli = attempt === 0 ? client : await getOssClient()
                const r = await cli.uploadPart(objectName, uploadId, partNumber, blob)
                uploadedSet.set(partNumber, { partNumber, etag: r.etag, size: blob.size })
                reportProgress()
                lastErr = null
                break
            } catch (e) {
                lastErr = e
                if (attempt >= MAX_PART_RETRY) break
                await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)))
            }
        }
        if (lastErr) throw lastErr
    }

    const sortedParts = Array.from(uploadedSet.values())
        .sort((a, b) => a.partNumber - b.partNumber)
        .map(p => ({ number: p.partNumber, etag: String(p.etag).replace(/"/g, '') }))

    const result = await client.completeMultipartUpload(objectName, uploadId, sortedParts)
    await completeUploadSession(businessKey)

    let url = ''
    if (result.res?.requestUrls?.length) url = result.res.requestUrls[0].split('?')[0]
    else if (result.url) url = result.url.split('?')[0]
    if (url.startsWith('http://')) url = url.replace('http://', 'https://')
    const signedUrl = await signFile(url)
    onProgress && onProgress(100)

    return { url: signedUrl, name: result.name || objectName, originalName: file.name }
}

/**
 * 业务封装：传 answerKey + itemKey 时走可断点续传通道（跨设备恢复），
 * 否则退化为普通 smartUpload。
 *
 * @param {File} file
 * @param {string} path
 * @param {{answerKey?: string, itemKey?: string}} ctx
 * @param {Function} onProgress
 */
export const smartUploadResumable = async (file, path, ctx = {}, onProgress) => {
    const { answerKey, itemKey } = ctx || {}
    if (!answerKey || !itemKey) {
        return uploadWithProgress(file, path, onProgress)
    }
    const fingerprint = await computeFileFingerprint(file)
    const businessKey = `${answerKey}_${itemKey}_${fingerprint}`
    return uploadResumable(file, path, businessKey, onProgress, {
        sessionType: 0,
        mimeType: file.type || ''
    })
}

export default {
    getOssClient,
    uploadFile,
    uploadWithProgress,
    smartUpload,
    deleteFile,
    signFile,
    uploadBlob,
    initMultipartUpload,
    uploadPart,
    completeMultipartUpload,
    abortMultipartUpload,
    listParts,
    computeFileFingerprint,
    getUploadSession,
    initUploadSession,
    completeUploadSession,
    abortUploadSession,
    uploadResumable,
    smartUploadResumable
}

